import { useState } from 'preact/hooks';
import { DEFAULT_BOT, type BotKind } from '../engine';
import { DEFAULT_SETTINGS, MAX_NAME_LENGTH, MAX_SEATS, type GameSettings } from '../shared/protocol';
import { GameScreen } from './game-screen';
import { loadLocalGame, newLocalGame, saveLocalGame, useLocalGame, type LocalGameSave } from './local-game';
import { uniqueSillyName } from '../shared/names';
import { PLAYER_PALETTE, defaultColors } from './theme';
import { BotPicker, ColorPicker, SettingsForm, beginnerAvailable, selectOnFocus } from './widgets';

function randomSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0]!;
}

function seedFromUrl(): number | null {
  const raw = new URLSearchParams(location.search).get('seed');
  const n = raw === null ? NaN : Number(raw);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/** Suggested names: silly ones, or the colour names under the `?seed=` test hook so tests can rely on them. */
function suggestNames(count: number, taken: readonly string[] = []): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const seat = taken.length + i;
    out.push(seedFromUrl() !== null ? (PLAYER_PALETTE[seat]?.name ?? `Player ${seat + 1}`) : uniqueSillyName([...taken, ...out]));
  }
  return out;
}

function Setup({ onStart }: { onStart: (save: LocalGameSave) => void }) {
  const [count, setCount] = useState(3);
  const [names, setNames] = useState<string[]>(() => suggestNames(6));
  const [colors, setColors] = useState<number[]>(() => defaultColors(6));
  const [cpu, setCpu] = useState<boolean[]>([]);
  const [bots, setBots] = useState<BotKind[]>([]);
  // `?seed=N` is the test hook for a deterministic game, so it also starts with the first seat instead of rolling.
  const [settings, setSettings] = useState<GameSettings>(() => (seedFromUrl() !== null ? { ...DEFAULT_SETTINGS, firstPlayer: 0 } : DEFAULT_SETTINGS));
  const [privacy, setPrivacy] = useState(true);

  const resize = (n: number) => {
    const next = Math.min(MAX_SEATS, Math.max(2, n));
    if (next > names.length) {
      setNames([...names, ...suggestNames(next - names.length, names)]);
      setColors([...colors, ...defaultColors(next - colors.length, colors)]);
    }
    setCount(next);
  };

  const start = (e: Event) => {
    e.preventDefault();
    const chosen = names.slice(0, count).map((n, i) => n.trim() || (PLAYER_PALETTE[colors[i] ?? i]?.name ?? `Player ${i + 1}`));
    const layout = beginnerAvailable(count, settings) ? settings.layout : 'random';
    onStart(
      newLocalGame({
        names: chosen,
        layout,
        seed: seedFromUrl() ?? randomSeed(),
        privacy,
        turnSeconds: settings.turnSeconds,
        rules: settings.rules,
        victoryPoints: settings.victoryPoints,
        mapSize: settings.mapSize,
        firstPlayer: settings.firstPlayer === undefined || (typeof settings.firstPlayer === 'number' && settings.firstPlayer >= count) ? 'roll' : settings.firstPlayer,
        cpu: Array.from({ length: count }, (_, i) => !!cpu[i]),
        colors: colors.slice(0, count),
        bots: Array.from({ length: count }, (_, i) => bots[i] ?? DEFAULT_BOT),
      }),
    );
  };

  return (
    <form class="panel setup" onSubmit={start}>
      <h2>Play on this device</h2>
      <p class="muted">Pass the phone or laptop around. Everyone plays from one screen.</p>
      <div class="row wrap">
        <span>Players</span>
        {[2, 3, 4, 5, 6].map((n) => (
          <button type="button" key={n} class={count === n ? 'active small' : 'secondary small'} data-testid={`count-${n}`} onClick={() => resize(n)}>
            {n}
          </button>
        ))}
        {count > 6 && (
          <button type="button" class="active small" data-testid={`count-${count}`}>
            {count}
          </button>
        )}
        <button type="button" class="secondary small" data-testid="add-player" aria-label="Add a player" disabled={count >= MAX_SEATS} onClick={() => resize(count + 1)}>
          +
        </button>
      </div>
      {count > 4 && <p class="muted small">The board, bank, and development deck grow for {count} players.</p>}
      {names.slice(0, count).map((name, i) => (
        <div class="row" key={i}>
          <ColorPicker
            value={colors[i] ?? i}
            taken={colors.slice(0, count).filter((_, j) => j !== i)}
            onChange={(c) => setColors(colors.map((x, j) => (j === i ? c : x)))}
            testid={`color-${i}`}
            label={`Colour for ${name || `player ${i + 1}`}`}
          />
          <input
            value={name}
            maxLength={MAX_NAME_LENGTH}
            aria-label={`Player ${i + 1} name`}
            data-testid={`name-${i}`}
            onFocus={selectOnFocus}
            onInput={(e) => setNames(names.map((n, j) => (j === i ? (e.target as HTMLInputElement).value : n)))}
          />
          <label class="cpu-toggle" title="Let the computer play this seat">
            <input
              type="checkbox"
              data-testid={`cpu-${i}`}
              checked={!!cpu[i]}
              onChange={(e) => {
                const next = cpu.slice();
                next[i] = (e.target as HTMLInputElement).checked;
                setCpu(next);
              }}
            />
            <span>CPU</span>
          </label>
          {cpu[i] && (
            <BotPicker
              value={bots[i] ?? DEFAULT_BOT}
              testid={`cpu-kind-${i}`}
              label={`Which CPU plays ${name || `player ${i + 1}`}`}
              onChange={(k) => {
                const next = bots.slice();
                next[i] = k;
                setBots(next);
              }}
            />
          )}
          {count > 2 && i === count - 1 && (
            <button type="button" class="small secondary" data-testid="remove-player" aria-label={`Remove ${name || `player ${i + 1}`}`} onClick={() => resize(count - 1)}>
              ×
            </button>
          )}
        </div>
      ))}
      <SettingsForm value={settings} onChange={setSettings} playerCount={count} seatNames={names.slice(0, count)} />
      <label class="check">
        <input type="checkbox" checked={privacy} data-testid="privacy" onChange={(e) => setPrivacy((e.target as HTMLInputElement).checked)} />
        <span>Hide hands between players</span>
      </label>
      <button type="submit" class="primary" data-testid="start-local">
        Start game
      </button>
    </form>
  );
}

function LocalGame({ initial, onExit }: { initial: LocalGameSave; onExit: () => void }) {
  const client = useLocalGame(initial);
  const [revealed, setRevealed] = useState<number | null>(null);
  // With one human (the rest CPUs) there's nobody to hide hands from.
  const humans = client.names.filter((_, i) => !client.cpu?.[i]).length;
  const hidden = client.save.setup.privacy && humans > 1 && revealed !== client.seat && client.view.phase.kind !== 'gameOver';

  if (hidden && client.seat !== null) {
    return (
      <div class="panel pass" data-testid="pass-device">
        <h2>Pass to {client.names[client.seat]}</h2>
        <p class="muted">Hand the device over, then tap to see your cards.</p>
        <button class="primary" data-testid="reveal" onClick={() => setRevealed(client.seat)}>
          I'm {client.names[client.seat]}
        </button>
      </div>
    );
  }
  return <GameScreen client={client} onExit={onExit} />;
}

export function LocalApp() {
  const [save, setSave] = useState<LocalGameSave | null>(loadLocalGame);
  const exit = () => {
    saveLocalGame(null);
    setSave(null);
  };
  if (!save) {
    return (
      <Setup
        onStart={(s) => {
          saveLocalGame(s);
          setSave(s);
        }}
      />
    );
  }
  // Key on the seed so starting a new game remounts the hook with fresh state.
  return <LocalGame key={save.setup.seed} initial={save} onExit={exit} />;
}
